import React, {useLayoutEffect, useRef} from "react";
import {Box, Button, styled} from "@reside/ui";

import {Spinner} from "../spinner";
import {ColumnBody, ColumnCardsPadding, ColumnHeader} from "./Column";
import {AdmissionCellList} from "./AdmissionCellList";
import {useStickyColumnHeaderDivider} from "./hooks";
import {useWorkflowsQuery} from "./hooks/useWorkflowsQuery";
import {readableAdmissionStatus} from "../admission-status-tag";
import {requestDashboardGridLayoutSync} from "./dashboardGridLayoutSync";
import type {WorkflowStatus} from "../../services/WorkflowsService";
import type {AdmissionFromSearch} from "../../services/AdmissionsService";

const plural = (
  totalElements: number,
  zero: string,
  one: string,
  other: string,
  isCapped = false,
) => {
  // The service stops counting at its cap, so a large column shows "10,000+".
  if (isCapped) {
    return `${totalElements.toLocaleString()}+ ${other}`;
  }

  switch (totalElements) {
    case 0:
      return zero;
    case 1:
      return `${totalElements} ${one}`;
    default:
      return `${totalElements.toLocaleString()} ${other}`;
  }
};

type Props = {
  status: WorkflowStatus;
  loadMoreButtonId: string;
  /** Filters, so a page cannot escape the current search. */
  facilityIds: ReadonlyArray<string> | null;
  query?: string | null;
  workflowType?: string | null;
  starredOnly?: boolean;
};

/**
 * One status column, with its own query — per column rather than one request for the board, so
 * a column expanded by Load More stays coherent when an approve or archive refetches it.
 */
export const DashboardGridColumn = ({
  status,
  loadMoreButtonId,
  facilityIds,
  query,
  workflowType,
  starredOnly,
}: Props) => {
  // `status` is already this column's WorkflowStatus prop, so the query's own is aliased.
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetching,
    status: queryStatus,
    appliedQuery,
  } = useWorkflowsQuery({
    status,
    facilityIds,
    query,
    workflowType,
    starredOnly,
  });

  const hasFacilities = (facilityIds?.length ?? 0) > 0;
  const cards = data?.pages.flatMap(page => page.cards) ?? [];
  // Every page carries the same total for these filters; the first is as good as the last.
  const [{total = 0, totalIsCapped = false} = {}] = data?.pages ?? [];

  const readableStatus = readableAdmissionStatus(status as any);

  const headerRef = useRef<HTMLElement>(null);
  const cardsPaddingRef = useRef<HTMLDivElement>(null);
  const headerDividerActive = useStickyColumnHeaderDivider(
    headerRef,
    cardsPaddingRef,
  );

  /*
   * Rendered from react-query's status rather than from "no cards and not fetching", so a
   * failed request cannot present as an empty column. That collapse is what hid errors here:
   * a 500 and a 422 both leave cards empty and isFetching false.
   *
   * Empty is still withheld while fetching, so a search does not flash "no matches" before
   * its results arrive.
   */
  const showError = hasFacilities && queryStatus === "error";
  const showEmptyState =
    hasFacilities && queryStatus === "success" && cards.length === 0 && !isFetching;

  useLayoutEffect(() => {
    requestDashboardGridLayoutSync();
  }, [cards.length, data?.pages.length, isFetching]);

  return (
    <ColumnBody data-test-id={`${readableStatus.toLowerCase()}-column`}>
      <ColumnHeader
        ref={headerRef}
        scrolledUnder={headerDividerActive}
        title={readableStatus}
        subtitle={plural(
          hasFacilities ? total : 0,
          "NO WORKFLOWS",
          "WORKFLOW",
          "WORKFLOWS",
          hasFacilities && totalIsCapped,
        )}
      />
      <ColumnCardsPadding ref={cardsPaddingRef}>
        {/* The card carries what the cells read — answers, flags, resources,
            templateDocumentTypes, completionPercentage, residentId, and `starred`, which the
            service resolves per caller from reside rather than from the replica. */}
        <AdmissionCellList
          admissions={(hasFacilities ? cards : []) as unknown as AdmissionFromSearch[]}
        />

        {showError && (
          <EmptyState data-test-id={`${readableStatus.toLowerCase()}-column-error`}>
            This column could not be loaded. Try again.
          </EmptyState>
        )}

        {showEmptyState && (
          <EmptyState data-test-id={`${readableStatus.toLowerCase()}-column-empty`}>
            {/* appliedQuery, not the raw prop: below the minimum length no term was sent, so
                naming it here would blame a search that never ran. */}
            {appliedQuery
              ? `No workflows match "${appliedQuery}"`
              : "No workflows in this column"}
          </EmptyState>
        )}

        <Box flexDirection="row" justifyContent="space-around">
          {hasNextPage && hasFacilities && !isFetching && (
            <Button
              size="small"
              color="primary"
              onClick={() => {
                fetchNextPage();
              }}
              testId={loadMoreButtonId}
            >
              Load More
            </Button>
          )}
          {isFetching && (
            <Offset>
              <Spinner relative />
            </Offset>
          )}
        </Box>
      </ColumnCardsPadding>
    </ColumnBody>
  );
};

const Offset = styled.div`
  margin-top: ${({theme}) => theme.space[6]}px;
`;

const EmptyState = styled.div`
  padding: ${({theme}) => theme.space[5]}px ${({theme}) => theme.space[4]}px;
  text-align: center;
  color: ${({theme}) => theme.color.gray100};
  font-size: ${({theme}) => theme.fontSize[1]}px;
  /* Wraps rather than stretching the column when the term is long. */
  overflow-wrap: anywhere;
`;
